import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import {
    Button,
    Container,
    FilterBarDropdown,
    Input,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useForm, usePage } from '@inertiajs/react';
import qs from 'qs';
import Navigation from './components/Navigation';

const FIELDS = [
    { name: 'email', label: 'E-mailadres', type: 'email' },
    { name: 'phone', label: 'Telefoonnummer', type: 'text' },
    { name: 'birth_date', label: 'Geboortedatum', type: 'date' },
];

const EditView = () => {
    const { auth, missing_data } = usePage().props;
    const { filters, updateFilters } = useFilters(['missing_data']);

    const { data, setData, patch, processing, transform, isDirty } = useForm(
        []
    );

    const showEntity = (filters?.entity_ids?.length || 0) > 1;
    const columns = showEntity ? 6 : 5;

    const handleChange = (id, field, value) => {
        if (!data?.some(datum => datum.id === id)) {
            setData([...(data || []), { id, [field]: value }]);

            return;
        }

        setData([
            ...(data?.map(datum => {
                if (datum.id === id) {
                    return {
                        ...datum,
                        [field]: value,
                    };
                }

                return datum;
            }) || []),
        ]);
    };

    const submit = () => {
        transform(data => ({ missing_datas: data }));

        patch(`/customers/${auth?.customer?.id}/services/missing-data/edit`, {
            preserveScroll: true,
            onSuccess: () => setData([]),
        });
    };

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header={`Ontbrekende gegevens bijwerken (${missing_data?.meta?.total || 0})`}
                >
                    <Button
                        as='a'
                        href={`/customers/${auth?.customer?.id}/services/missing-data/export?${qs.stringify(
                            {
                                entity_ids: filters?.entity_ids,
                            }
                        )}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        <ArrowDownTrayIcon className='size-5' />
                        Exporteren
                    </Button>
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['missing_data']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                <TableHeaderCell>Vestiging</TableHeaderCell>
                                {showEntity && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                {FIELDS.map(field => (
                                    <TableHeaderCell key={field.name}>
                                        {field.label}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {missing_data?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={columns} />
                            )}

                            {missing_data?.data?.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <EmployeeCard
                                            employee={row?.employee}
                                            link
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {row.employee?.office?.name}
                                    </TableCell>
                                    {showEntity && (
                                        <TableCell>
                                            {row.entity?.name}
                                        </TableCell>
                                    )}
                                    {FIELDS.map(field => (
                                        <TableCell key={field.name}>
                                            {row.requested_fields?.includes(
                                                field.name
                                            ) ? (
                                                <Input
                                                    type={field.type}
                                                    aria-label={`${field.label} ${row.employee?.name}`}
                                                    value={
                                                        data?.find(
                                                            item =>
                                                                item.id ===
                                                                row.id
                                                        )?.[field.name] || ''
                                                    }
                                                    onChange={event =>
                                                        handleChange(
                                                            row.id,
                                                            field.name,
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            ) : (
                                                'n.v.t'
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter className='flex items-center justify-between gap-4'>
                    <CustomPagination
                        meta={missing_data?.meta}
                        only={['missing_data']}
                    />
                    <Button
                        variant='primary'
                        onClick={submit}
                        loading={processing}
                        disabled={!isDirty}
                    >
                        Opslaan
                    </Button>
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default EditView;
